import React, { useEffect, useMemo, useState } from "react";

import * as API from "@ekonsilio/api-livechat";
import classNames from "classnames";
import { decode } from "html-entities";
import PropTypes from "prop-types";
import { useMutation } from "react-query";

import generateSentAtFromLastMessage from "../../../utils/helpers/generateSentAtFromLastMessage";
import { getChoiceMessagesSessionStorage } from "../../../utils/session-storage";
import { useWebmessenger } from "../../../utils/webmessenger";
import Markdown from "../../markdown/index";

const webmessengerApi = new API.Messaging.Webmessenger();

function MessageChoice({ _id, content, author, first, last, status }) {
    const [alreadyClicked, setAlreadyClicked] = useState(false);

    const { conversation, messages } = useWebmessenger();

    const latestMessage = useMemo(() => {
        if (!messages?.length) return null;
        return messages[messages.length - 1];
    }, [messages]);

    const { mutate: sendMessage } = useMutation(
        async (message) => await webmessengerApi.sendMessage(message)
    );

    const handleClickChoiceMessage = (value) => {
        const sentAt = generateSentAtFromLastMessage(latestMessage);

        sendMessage({
            type: "text",
            content: value,
            author: {
                type: "visitor",
            },
            conversation: conversation._id,
            status: {
                sentAt,
            },
        });

        const storage = getChoiceMessagesSessionStorage();

        const newStorage = {
            ...storage,
            [_id]: true,
        };

        sessionStorage.setItem(
            "eK-chat-choice-messages",
            JSON.stringify(newStorage)
        );

        setAlreadyClicked(true);
    };

    useEffect(() => {
        const storage = getChoiceMessagesSessionStorage();

        if (storage && storage[_id]) setAlreadyClicked(true);
    }, []);

    return (
        <div
            className={classNames("ek-chat__message--text", {
                "ek-chat__message--text-first":
                    first &&
                    (!last ||
                        author.type === "visitor" ||
                        (author.type !== "visitor" && last)),
                "ek-chat__message--text-last": last && !first,
                "ek-chat__message--text-loading": !!status?.loading,
                "ek-chat__message--text-error": !!status?.error,
            })}
        >
            <Markdown content={decode(content.text.trim())} />
            {!alreadyClicked && content.interactions.length > 0 && (
                <div className="ek-chat__message--interactions">
                    {content.interactions.map(
                        (interaction, interactionIndex) => (
                            <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={`interaction-${interactionIndex}`}
                            >
                                {interaction.elements &&
                                    interaction.elements.length > 0 && (
                                        <div className="ek-chat__message--interaction-elements">
                                            {interaction.elements.map(
                                                (element) => (
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            conversation.status ===
                                                            "closed"
                                                        }
                                                        key={element.value}
                                                        className="ek-chat__message--interaction-element"
                                                        onClick={() => {
                                                            if (
                                                                conversation.status !==
                                                                "closed"
                                                            )
                                                                handleClickChoiceMessage(
                                                                    element.value
                                                                );
                                                        }}
                                                    >
                                                        {element.label}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    )}
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

MessageChoice.propTypes = {
    _id: PropTypes.string.isRequired,
    content: PropTypes.shape({
        text: PropTypes.string,
        interactions: PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.string,
                elements: PropTypes.arrayOf(
                    PropTypes.shape({
                        type: PropTypes.string,
                        label: PropTypes.string,
                        value: PropTypes.oneOfType([
                            PropTypes.string,
                            PropTypes.number,
                        ]),
                    })
                ),
            })
        ),
    }).isRequired,
    author: PropTypes.shape({
        _id: PropTypes.string,
        type: PropTypes.oneOf(["operator", "supervisor", "visitor", "agent"]),
        name: PropTypes.string,
    }),
    status: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
    first: PropTypes.bool,
    last: PropTypes.bool,
};

MessageChoice.defaultProps = {
    author: {},
    status: {},
    first: false,
    last: false,
};

export default MessageChoice;
